import RouteTemplate from 'ember-route-template'
import { t } from 'ember-intl'
import type LongWritesPreviewsRoute from 'teacher-ui-client/subscription-type/long-writes/previews/route'
import Component from '@glimmer/component'
import NoResultsMessage from 'teacher-ui-client/components/no-results-message.gts'
import PageHeader from 'teacher-ui-client/components/page-header/index.gts'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon.gts'
import { LinkTo } from '@ember/routing'
import UiButtonLinkTo from 'teacher-ui-client/components/ui-button/link-to.gts'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { array } from '@ember/helper'
import { hash } from '@ember/helper'

interface Signature {
  Args: {
    model: ModelFor<LongWritesPreviewsRoute>
  }
}

function getWLAudioFullPath(relativePath: string): string {
  return `https://cdn-editor.writinglegends.com/${relativePath}`
}

export class LongWritesPreviewsPage extends Component<Signature> {
  get activityId() {
    return this.args.model?.params.activity_id
  }

  get podcast() {
    const blockContent = this.args.model?.activity?.blocks?.find((block) => block.block_type === 'Podcast')?.content
    if (blockContent) {
      const podcastContent = JSON.parse(blockContent) as { podcast1: { src: string } }
      return getWLAudioFullPath(podcastContent.podcast1.src)
    }
    return null
  }

  get forAndAgainst() {
    const blockContent = this.args.model?.activity?.blocks?.find(
      (block) => block.block_type === 'lw_text_for_and_against',
    )?.content

    if (blockContent) {
      return JSON.parse(blockContent) as { forArguments: string[]; againstArguments: string[] }
    }
    return { forArguments: [], againstArguments: [] }
  }

  get rows() {
    const maxRowsLength = Math.max(this.forAndAgainst.forArguments.length, this.forAndAgainst.againstArguments.length)
    const rows: Array<{ index: number; forText?: string; againstText?: string }> = []
    for (let i = 0; i < maxRowsLength; i++) {
      rows.push({
        index: i + 1,
        forText: this.forAndAgainst.forArguments[i],
        againstText: this.forAndAgainst.againstArguments[i],
      })
    }
    return rows
  }
  <template>
    <div class="grow">
      <PageHeader>
        <:left>
          <LinkTo @route="subscription-type.long-writes.index"><PhosphorIcon @icon="phosphor-caret-left" /><span
              class="underline"
            >{{t "back"}}</span></LinkTo>
        </:left>
        <:title>
          <h1>
            {{t "pages.longWrites.title"}}
          </h1>
        </:title>
      </PageHeader>
      {{#if @model.activity}}
        <div class="mx-auto flex w-2/3 flex-col">
          <UiButtonLinkTo
            @route="assignments.product.create"
            @models={{array "writing-legends"}}
            @query={{hash activityType="writing-legends-long-writes" writingCode=this.activityId}}
            @appearance={{ButtonAppearance.regular}}
            class="mx-auto mb-6"
          >
            <PhosphorIcon @icon="phosphor-calendar-check-fill" class="mr-2" />
            {{t "assignments.create.writingAssignments.longWrites.assignLabel"}}
          </UiButtonLinkTo>
          <h1 class="m-0 p-0 text-2xl">{{@model.activity.title}}</h1>
          <h4 class="text-lg font-normal">{{@model.activity.description}}</h4>
          <div class="my-4 flex items-center">
            <div class="w-44">{{t "pages.longWrites.previews.playPodcast"}}</div>
            {{#if this.podcast}}
              {{! template-lint-disable require-media-caption }}
              {{! Cannot caption user-generated audio }}
              <audio class="my-4 w-full" controls>
                <source src={{this.podcast}} type="audio/mpeg" />
              </audio>
            {{/if}}
          </div>
          <table>
            <thead>
              <tr>
                <th class="p-2 text-left text-xl">{{t "pages.longWrites.previews.forArguments"}}</th>
                <th class="p-2 text-left text-xl">{{t "pages.longWrites.previews.againstArguments"}}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.rows as |row|}}
                <tr>
                  <td class="pr-4 pb-6 align-top">
                    {{#if row.forText}}
                      {{row.index}}.
                      {{row.forText}}
                    {{/if}}
                  </td>
                  <td class="pr-4 pb-6 align-top">
                    {{#if row.againstText}}
                      {{row.index}}.
                      {{row.againstText}}
                    {{/if}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      {{else}}
        <NoResultsMessage />
      {{/if}}
    </div>
  </template>
}
export default RouteTemplate(LongWritesPreviewsPage)
